import React, { useState, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHeart,
  faUsers,
  faSeedling,
  faPlus,
  faArrowRight,
  faChevronDown,
  faChevronUp,
  faEdit,
  faTrash,
  faDownload,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";

export default function SheEarnsGoalBoardHome() {
  // 🌸 Dummy data for SheEarns & SAKSHAM Shakti goals
  const initialGoals = [
    {
      id: 1,
      title: "Empower 1000 Women Entrepreneurs",
      description: "Promote digital inclusion and income generation for women through SheEarns.",
      progress: 72,
      quarter: "Q4-2025",
      status: "On Track",
      kras: [
        {
          id: 101,
          title: "Digital Onboarding & Training",
          weight: 50,
          progress: 80,
          metrics: [
            { id: 1001, name: "Women Onboarded", value: 720, target: 1000, unit: "participants" },
          ],
        },
        {
          id: 102,
          title: "Mentorship Circles",
          weight: 50,
          progress: 64,
          metrics: [
            { id: 1002, name: "Active Mentorship Groups", value: 16, target: 25, unit: "groups" },
          ],
        },
      ],
    },
    {
      id: 2,
      title: "Launch 10 SAKSHAM Workshops",
      description: "Upskill women in financial literacy, leadership, and self-reliance.",
      progress: 58,
      quarter: "Q4-2025",
      status: "At Risk",
      kras: [
        {
          id: 201,
          title: "Workshop Outreach",
          weight: 60,
          progress: 55,
          metrics: [
            { id: 2001, name: "Workshops Conducted", value: 6, target: 10, unit: "sessions" },
          ],
        },
        {
          id: 202,
          title: "Participant Feedback",
          weight: 40,
          progress: 61,
          metrics: [
            { id: 2002, name: "Positive Feedback (%)", value: 78, target: 90, unit: "%" },
          ],
        },
      ],
    },
  ];

  const [goals, setGoals] = useState(initialGoals);
  const [expandedGoalId, setExpandedGoalId] = useState(null);

  const avgProgress = useMemo(
    () => Math.round(goals.reduce((s, g) => s + g.progress, 0) / goals.length),
    [goals]
  );

  // 🌸 UI starts
  return (
    <div className="min-h-screen bg-gray-900 text-pink-100 p-4 sm:p-6">
      {/* Header */}
      <header className="text-center sm:text-left mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-pink-300 flex items-center justify-center sm:justify-start gap-2">
          <FontAwesomeIcon icon={faHeart} className="text-pink-400" />
          SheEarns Goal Board
        </h1>
        <p className="text-sm text-pink-400">Empowerment • Inclusion • Growth</p>
      </header>

      {/* Summary */}
      <section className="bg-gray-700 rounded-2xl p-4 mb-6 border border-pink-800 text-center sm:text-left">
        <h2 className="text-lg font-semibold text-pink-200">Overall Progress</h2>
        <div className="text-3xl font-bold text-pink-400 mt-1">{avgProgress}%</div>
        <div className="w-full h-3 bg-pink-800 rounded mt-3 overflow-hidden">
          <div
            className="h-3 bg-pink-500 rounded"
            style={{ width: `${avgProgress}%` }}
          ></div>
        </div>
      </section>

      {/* Goals list */}
      <div className="space-y-4">
        {goals.map((goal) => (
          <div
            key={goal.id}
            className="bg-black rounded-2xl border border-pink-800 p-4 shadow-lg"
          >
            <div className="flex flex-col sm:flex-row sm:justify-between gap-3">
              <div>
                <h3 className="text-lg font-semibold text-pink-200">
                  {goal.title}
                </h3>
                <p className="text-sm text-pink-400">{goal.description}</p>
                <p className="text-xs text-pink-400 mt-1">
                  {goal.quarter} •{" "}
                  <span
                    className={
                      goal.status === "At Risk" ? "text-yellow-400" : "text-pink-300"
                    }
                  >
                    {goal.status}
                  </span>
                </p>
              </div>
              <div className="text-center sm:text-right">
                <div className="text-xs text-pink-400">Progress</div>
                <div className="text-2xl font-bold text-pink-300">
                  {goal.progress}%
                </div>
              </div>
            </div>

            {/* KRAs */}
            <div className="mt-3 space-y-3">
              {goal.kras.map((kra) => (
                <div
                  key={kra.id}
                  className="bg-black rounded-lg p-3 border border-pink-800"
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <h4 className="font-semibold text-pink-100">{kra.title}</h4>
                      <p className="text-xs text-pink-400">{kra.weight}% Weight</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-pink-400">Progress</p>
                      <p className="font-bold text-pink-300">{kra.progress}%</p>
                    </div>
                  </div>

                  <div className="w-full h-2 bg-pink-800 rounded mt-2 overflow-hidden">
                    <div
                      className="h-2 bg-pink-500"
                      style={{ width: `${kra.progress}%` }}
                    ></div>
                  </div>

                  {/* Metrics */}
                  <div className="mt-2">
                    {kra.metrics.map((m) => (
                      <div
                        key={m.id}
                        className="flex justify-between text-xs text-pink-400"
                      >
                        <span>{m.name}</span>
                        <span>
                          {m.value} / {m.target} {m.unit}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Action */}
            <div className="mt-4 flex flex-row gap-2 justify-between place-items-center">
              <button
                onClick={() =>
                  setExpandedGoalId(expandedGoalId === goal.id ? null : goal.id)
                }
                className="flex-1 bg-pink-700 hover:bg-pink-600 py-2 rounded-lg flex items-center justify-center gap-2 text-sm"
              >
                <FontAwesomeIcon
                  icon={expandedGoalId === goal.id ? faChevronUp : faChevronDown}
                />
                {expandedGoalId === goal.id ? "Collapse" : "View Details"}
              </button>

              <button className="bg-pink-800 hover:bg-pink-700 w-10 px-3 py-2 rounded-lg text-sm">
                <FontAwesomeIcon icon={faEdit} />
              </button>
              <button className="bg-red-700 hover:bg-red-600 w-10 px-3 py-2 rounded-lg text-sm">
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
